import type { ILead } from 'src/services/api/leads/types';
import type { IProperty } from 'src/services/api/properties/types';
import type { AmiIncomeLimit } from 'src/services/api/properties/ami-income-limits';
import { parsePropertyAmiIncomeLimits } from 'src/services/api/properties/ami-income-limits';
import type { IAmiIncomeLimitsResponse } from 'src/services/api/ami/types';

/**
 * How a lead's income compares to a band's income limit.
 * `unknown` when we lack the income or the limit needed to compare.
 */
export type AmiBandStatus = 'below' | 'near' | 'over' | 'unknown';

export interface AmiBandRow {
    /** Band as a display string, e.g. "60%". */
    bandLabel: string;
    /** Numeric band percent, used for ordering, e.g. 60. */
    bandPercent: number;
    /** Annual income limit for the lead's household size, or null if unknown. */
    amount: number | null;
    status: AmiBandStatus;
}

/** Income at or below this fraction of the limit is comfortably eligible (green). */
const NEAR_LOWER = 0.95;
/** Income at or below this fraction of the limit is borderline (orange); above is over (red). */
const NEAR_UPPER = 1.05;

export const getAmiBandStatus = (
    annualIncome: number | null | undefined,
    amount: number | null
): AmiBandStatus => {
    if (annualIncome === null || annualIncome === undefined || amount === null || amount <= 0) {
        return 'unknown';
    }
    const ratio = annualIncome / amount;
    if (ratio < NEAR_LOWER) {
        return 'below';
    }
    if (ratio <= NEAR_UPPER) {
        return 'near';
    }
    return 'over';
};

const bandPercentFromLabel = (label: string): number => Number(label.replace('%', '').trim());

const resolveFromFact = (
    factLimits: AmiIncomeLimit[],
    householdSize: number | null
): AmiBandRow[] => {
    const entriesByBand = new Map<string, AmiIncomeLimit[]>();
    for (const entry of factLimits) {
        const existing = entriesByBand.get(entry.band) ?? [];
        existing.push(entry);
        entriesByBand.set(entry.band, existing);
    }

    const rows: AmiBandRow[] = [];
    for (const [band, entries] of entriesByBand) {
        const match =
            householdSize === null ?
                undefined :
                entries.find((entry) => entry.household_size === householdSize);
        rows.push({
            bandLabel: band,
            bandPercent: bandPercentFromLabel(band),
            amount: match ? match.amount : null,
            status: 'unknown',
        });
    }
    return rows;
};

const resolveFromGlobal = (
    globalLimits: IAmiIncomeLimitsResponse,
    householdSize: number | null
): AmiBandRow[] => {
    const sizes = Object.keys(globalLimits.limits).map(Number);
    if (sizes.length === 0) {
        return [];
    }

    const hasAmount = householdSize !== null && globalLimits.limits[householdSize] !== undefined;
    // With a household size we get just that size; without one we get the full
    // grid and only enumerate the bands (from any size) with no amount.
    const bandSource = hasAmount ? householdSize : sizes[0];
    const bandMap = globalLimits.limits[bandSource as number];

    const rows: AmiBandRow[] = [];
    for (const bandStr of Object.keys(bandMap)) {
        const bandPercent = Number(bandStr);
        rows.push({
            bandLabel: `${bandPercent}%`,
            bandPercent,
            amount: hasAmount ? bandMap[bandPercent] : null,
            status: 'unknown',
        });
    }
    return rows;
};

/**
 * Whether the fact supplies a limit for the household size for *every* band it
 * lists. A partially-populated fact (e.g. a size present for the 60% band but
 * not the 80% band) is not considered covering, so we fall back to the global
 * limits rather than dash the uncovered bands.
 */
const factCoversHousehold = (
    factLimits: AmiIncomeLimit[],
    householdSize: number | null
): boolean => {
    if (householdSize === null) {
        return false;
    }
    const bandsWithSize = new Set(
        factLimits
            .filter((entry) => entry.household_size === householdSize)
            .map((entry) => entry.band)
    );
    return factLimits.every((entry) => bandsWithSize.has(entry.band));
};

/** Whether the AMI bands card should render at all for this property. */
export const shouldShowAmiCard = (property: IProperty): boolean => {
    const factLimits = parsePropertyAmiIncomeLimits(property) ?? [];
    return property.is_income_capped || factLimits.length > 0;
};

/**
 * Whether we need to fetch the global county limits. True when the card is shown
 * but the property fact can't supply the bands for the lead's household size —
 * either there is no usable fact, or the fact has rows but none for that size.
 */
export const needsGlobalLimits = (property: IProperty, lead: ILead): boolean => {
    const factLimits = parsePropertyAmiIncomeLimits(property) ?? [];
    const hasFact = factLimits.length > 0;
    if (!(property.is_income_capped || hasFact)) {
        return false;
    }
    if (!hasFact) {
        return true;
    }
    return lead.household_size !== null && !factCoversHousehold(factLimits, lead.household_size);
};

/**
 * Resolve the AMI bands to display for a lead on a property, colored by how the
 * lead's annual income compares to each band's limit at their household size.
 *
 * The property fact is preferred, but only when it actually covers the lead's
 * household size (or the household size is unknown, where no source can supply
 * amounts). Otherwise the global county limits are used so an incomplete fact
 * doesn't leave the lead staring at dashes when the county endpoint has the
 * data; the fact is used as a best-effort fallback only when no global limits
 * are available. Rows are sorted descending by band percent (highest first).
 */
export const resolveAmiBands = (args: {
    property: IProperty;
    lead: ILead;
    globalLimits?: IAmiIncomeLimitsResponse;
}): AmiBandRow[] => {
    const { property, lead, globalLimits } = args;
    const householdSize = lead.household_size;
    const factLimits = parsePropertyAmiIncomeLimits(property) ?? [];
    const hasFact = factLimits.length > 0;
    const factUsable =
        hasFact && (householdSize === null || factCoversHousehold(factLimits, householdSize));

    let rows: AmiBandRow[] = [];
    if (factUsable) {
        rows = resolveFromFact(factLimits, householdSize);
    } else if (globalLimits) {
        rows = resolveFromGlobal(globalLimits, householdSize);
    } else if (hasFact) {
        rows = resolveFromFact(factLimits, householdSize);
    }

    return rows
        .map((row) => ({ ...row, status: getAmiBandStatus(lead.annual_income, row.amount) }))
        .sort((a, b) => b.bandPercent - a.bandPercent);
};
